import type { Key } from 'react';
import type { TopicType } from '../pages/topics/Topics.types.ts';

type Node = {
  id: number | string;
  title: string;
  abbreviation: string;
  parentAbbreviation: string;
  children?: Node[];
  key: Key;
  selectable: boolean;
  checkable: boolean;
  value: number | string;
};

export function getEnvVar(name: keyof ImportMetaEnv): string {
  const value = import.meta.env[name];
  if (!value) {
    throw new Error(`Missing environment variable: ${name}`);
  }
  return value;
}

type TreeNode<T> = T & {
  children?: TreeNode<T>[];
};

export function buildTree<T extends { id: string; name: string; parentId: string | null }>(
  items: T[],
  parentId: string | null = null,
): TreeNode<T>[] {
  return items
    ?.filter((item) => item.parentId === parentId)
    ?.map((item) => {
      const children = buildTree(items, item.id);
      return {
        ...item,
        key: item.id,
        title: item.name,
        expandable: Boolean(children.length),
        ...(children.length ? { children } : {}),
      };
    });
}

export function buildTreeByAbbreviation(data: Node[]): Node[] {
  const abbreviationMap = new Map<string, Node>();

  for (const item of data) {
    abbreviationMap.set(item.abbreviation, { ...item, children: [], value: item.id });
  }

  const tree: Node[] = [];

  for (const item of data) {
    const node = abbreviationMap.get(item.abbreviation)!;
    const parentAbbr = item.parentAbbreviation;

    if (parentAbbr && abbreviationMap.has(parentAbbr)) {
      const parent = abbreviationMap.get(parentAbbr)!;
      parent.children!.push(node);
      parent.checkable = false;
    } else {
      tree.push(node);
    }
  }

  return tree;
}

export function getParentTopic(
  node: TopicType,
  parents: TopicType[] = [],
  nodes: TopicType[] = [],
): TopicType[] {
  if (node?.parentTopic) {
    parents.push(node.parentTopic);
    return getParentTopic(node.parentTopic, parents, nodes);
  }
  if (node?.parentTopicId) {
    const parent = nodes.find((n) => n.id === node.parentTopicId);
    if (parent) {
      parents.push(parent);
      return getParentTopic(parent, parents, nodes);
    }
  }
  return parents;
}